import React, { useState, useEffect, useCallback, useRef } from "react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";

import { getTheme } from "./dashboard/theme";
import Header from "./dashboard/Header";
import Kpis from "./dashboard/Kpis";
import ChartFunnel from "./dashboard/ChartFunnel";
import Metrics from "./dashboard/Metrics";
import Operations from "./dashboard/Operations";
import SettingsMenu from "../CrmKanban/kanban/SettingsMenu";

const SETTINGS_KEY = "crmDashboardSettings";
const DEFAULT_SETTINGS = {
  variant: "funnel",
  densidade: "rico",
  slaLimite: 60,
  mostrarMetricas: true,
};

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

const ensureFonts = () => {
  const id = "crm-dashboard-fonts";
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;800;900&family=Outfit:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap";
  document.head.appendChild(link);
};

const CrmDashboard = () => {
  const embed = sessionStorage.getItem("whaticketEmbed") === "1";

  const [settings, setSettings] = useState(loadSettings);
  const [settingsAnchor, setSettingsAnchor] = useState(null);

  const [connFilter, setConnFilter] = useState("");
  const [agFilter, setAgFilter] = useState("");
  const [period, setPeriod] = useState("sete");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef(null);

  useEffect(() => {
    ensureFonts();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      /* localStorage indisponível */
    }
  }, [settings]);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (connFilter) params.whatsappId = connFilter;
      if (agFilter) params.userId = agFilter;
      const { data: res } = await api.get("/crm/dashboard", { params });
      setData(res);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [connFilter, agFilter]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  // Atualiza em eventos de lead/mensagem (debounced) + poll de 60s.
  useEffect(() => {
    const socket = openSocket();
    const bump = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(fetchData, 1500);
    };
    socket.on("crmLead", bump);
    socket.on("appMessage", bump);
    socket.on("ticket", bump);
    const iv = setInterval(fetchData, 60000);
    return () => {
      socket.disconnect();
      clearInterval(iv);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchData]);

  const V = getTheme(settings.variant);
  const agFilterActive = !!agFilter;
  const periodLabel = period === "hoje" ? "hoje" : "últimos 7 dias";
  const chart = data && (period === "hoje" ? data.chartToday : data.chart7d);

  return (
    <div
      style={{
        fontFamily: V.font,
        background: V.pageBg,
        minHeight: embed ? "100vh" : "calc(100vh - 48px)",
        height: embed ? "100vh" : "auto",
        overflowY: "auto",
        padding: "18px 22px 24px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          maxWidth: 1220,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {data && (
          <Header
            V={V}
            connections={data.filters.connections}
            attendants={data.filters.attendants}
            connFilter={connFilter}
            setConnFilter={setConnFilter}
            agFilter={agFilter}
            setAgFilter={setAgFilter}
            period={period}
            setPeriod={setPeriod}
            onOpenSettings={setSettingsAnchor}
          />
        )}

        {loading && !data && (
          <div style={{ padding: 40, textAlign: "center", color: V.sublabel }}>
            Carregando…
          </div>
        )}

        {data && (
          <>
            <Kpis V={V} kpis={data.kpis} agFilterActive={agFilterActive} />
            <ChartFunnel
              V={V}
              chart={chart}
              periodLabel={periodLabel}
              funnel={data.funnel}
              funnelOpenTotal={data.funnelOpenTotal}
              conversao={data.conversao}
            />
            <Metrics
              V={V}
              firstResponse={data.firstResponse}
              leadsPorAtendente={data.leadsPorAtendente}
              desempenho={data.desempenho}
              leadsPorTag={data.leadsPorTag}
              canais={data.canais}
              totalLeads={data.totalLeads}
              agFilterActive={agFilterActive}
            />
            <Operations
              V={V}
              atendentes={data.atendentes}
              atendentesOnline={data.atendentesOnline}
              heatmap={data.heatmap}
              semRespostaPorConexao={data.semRespostaPorConexao}
            />
          </>
        )}
      </div>

      <SettingsMenu
        open={!!settingsAnchor}
        anchorPosition={settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        settings={settings}
        onChange={setSettings}
      />
    </div>
  );
};

export default CrmDashboard;
